import { useEffect, useState } from "react";

import {
  faChevronDown,
  faCircleInfo,
  faExclamationTriangle,
  faPlug,
  faPlus,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import { capitalize, get } from "lodash-es";
import { UseFormSetValue, useFormContext } from "react-hook-form";

import {
  ManifestIntegrationProvider,
  ManifestJSONSchema,
} from "src/api/connectApi/manifestTypes";
import { Connection } from "src/api/connectApi/types";
import { Divider } from "src/base-components/Divider";
import { DropDown } from "src/base-components/DropDown";
import { CustomPopover } from "src/base-components/Popover";
import { Tabs } from "src/base-components/Tabs";
import {
  backupCredentialsSchema,
  hasBackupValues,
  isBackupInputKey,
  primaryCredentialsSchema,
  schemaSupportsBackupCredentials,
} from "src/connections/config/manifest/backupInputs";
import { Form } from "src/connections/config/manifest/common/Form";
import { ManifestFormType } from "src/connections/config/manifest/types";
import { Button } from "src/design-system/Button";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { hasEnvironmentEnabled } from "src/router/featureFlags";

type AddCredentialsDropdownProps = {
  environments: string[];
  environmentDisplayNamesMapping: Record<string, string>;
  onAddEnvironment: (environment: string) => void;
};

const AddCredentialsDropdown: React.FC<AddCredentialsDropdownProps> = ({
  environments,
  environmentDisplayNamesMapping,
  onAddEnvironment,
}) => (
  <CustomPopover
    button={
      <Button
        dataLoc="add-environment-to-manifest-connection"
        iconLeft={faPlus}
        iconRight={faChevronDown}
        variant="secondary"
      >
        Add credentials
      </Button>
    }
    dataLoc="manifest-connection-add-environment"
    placement="bottom-start"
  >
    <div className="flex flex-col py-2">
      {environments.map((environment) => (
        <CustomPopover.Button
          key={environment}
          as="button"
          className="whitespace-nowrap px-4 py-3 text-left font-inter-normal-13px text-gray-800 hover:bg-gray-50"
          data-loc={`add-${environment}-environment-to-manifest-connection`}
          onClick={() => onAddEnvironment(environment)}
        >
          Add {environmentDisplayNamesMapping[environment]} credentials
        </CustomPopover.Button>
      ))}
    </div>
  </CustomPopover>
);

type EnvironmentDropdownProps = {
  availableEnvironments: string[];
  addedEnvironments: string[];
  handleAddEnvironment: (environment: string) => void;
  environmentDisplayNamesMapping: Record<string, string>;
};

const EnvironmentDropDown = ({
  availableEnvironments,
  addedEnvironments,
  handleAddEnvironment,
  environmentDisplayNamesMapping,
}: EnvironmentDropdownProps) => {
  return (
    <DropDown
      buttonClassName="border-0 font-inter-semibold-13px text-gray-800 !ring-0"
      elements={availableEnvironments
        .filter((env) => !addedEnvironments.includes(env))
        .map((env) => ({
          key: env,
          value: env,
        }))}
      placement="bottomLeft"
      renderButtonValue={() => (
        <div
          className="flex items-center justify-between"
          data-loc="additional-manifest-connection-environment-button"
        >
          <Icon icon={faPlus} size="xs" />
          <span>Add credentials</span>
        </div>
      )}
      renderValue={({ value }) => (
        <div
          className="flex items-center justify-between whitespace-nowrap p-4 py-3"
          data-loc={`add-${value}-environment-to-manifest-connection`}
        >
          <span>Add {environmentDisplayNamesMapping[value]} credentials</span>
        </div>
      )}
      variant="transparent"
      onSelect={(value) => handleAddEnvironment(value)}
    />
  );
};

type EmptyEnvironmentMessageProps = {
  availableEnvironments: string[];
  providerDisplayName: string;
  handleAddEnvironment: (environment: string) => void;
  environmentDisplayNamesMapping: Record<string, string>;
};

const NoAddedEnvironmentMessage = ({
  availableEnvironments,
  providerDisplayName,
  handleAddEnvironment,
  environmentDisplayNamesMapping,
}: EmptyEnvironmentMessageProps) => (
  <>
    <div className="flex w-full flex-col items-center justify-center rounded-lg bg-gray-50 pt-14 text-center">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-100">
        <Icon icon={faPlug} size="sm" variant="active" />
      </div>
      <span className="mb-2 font-inter-medium-13px text-gray-900">
        No credentials added
      </span>
      <span className="mb-6 max-w-[240px] font-inter-normal-12px text-gray-500">
        Add an environment and configure your {providerDisplayName} credentials
      </span>
      <div className="mb-10">
        <AddCredentialsDropdown
          environmentDisplayNamesMapping={environmentDisplayNamesMapping}
          environments={availableEnvironments}
          onAddEnvironment={handleAddEnvironment}
        />
      </div>
    </div>
    <Divider spacing="my-4" />
  </>
);

const ADD_BACKUP_CREDENTIALS_TOOLTIP =
  "This provider supports credential refresh using Backup credentials. During a refresh, Taktile switches traffic to your Backup credentials to avoid interrupting your Decision Flows.";

const BACKUP_CREDENTIALS_DOCS_URL =
  "https://docs.taktile.com/integrations/automatic-credential-refresh";

const CredentialsSectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <span className="font-inter-medium-13px text-gray-500 uppercase">
    {children}
  </span>
);

type EnvironmentCredentialsFieldsProps = {
  environment: string;
  environmentDisplayName: string;
  schema: ManifestJSONSchema | undefined;
  isConnectionUpdate: boolean;
  isBackupSectionOpen: boolean;
  onAddBackupCredentials: (environment: string) => void;
  onRemoveBackupCredentials: (environment: string) => void;
  onRemoveEnvironment: (environment: string) => void;
};

const EnvironmentCredentialsFields: React.FC<
  EnvironmentCredentialsFieldsProps
> = ({
  environment,
  environmentDisplayName,
  schema,
  isConnectionUpdate,
  isBackupSectionOpen,
  onAddBackupCredentials,
  onRemoveBackupCredentials,
  onRemoveEnvironment,
}) => {
  const prefix = `manifest-connection-${environment}-config-form`;
  const supportsBackupCredentials = schemaSupportsBackupCredentials(schema);

  const removeEnvironmentButton = (
    <div className="mt-4 flex w-fit">
      <Button
        iconLeft={faTrashCan}
        size="sm"
        variant="secondary"
        onClick={(e) => {
          e.currentTarget.blur();
          onRemoveEnvironment(environment);
        }}
      >
        Remove {environmentDisplayName} credentials
      </Button>
    </div>
  );

  if (!supportsBackupCredentials || !schema) {
    return (
      <>
        <Form
          descriptionVariant="tooltip"
          formPath={`inputs.${environment}`}
          isConnectionUpdate={isConnectionUpdate}
          prefix={prefix}
          schema={schema}
        />
        {removeEnvironmentButton}
      </>
    );
  }

  return (
    <>
      <div className="mb-4">
        <CredentialsSectionTitle>Primary credentials</CredentialsSectionTitle>
      </div>
      <Form
        descriptionVariant="tooltip"
        formPath={`inputs.${environment}`}
        isConnectionUpdate={isConnectionUpdate}
        prefix={prefix}
        schema={primaryCredentialsSchema(schema)}
      />
      {removeEnvironmentButton}
      {isBackupSectionOpen ? (
        <div data-loc={`${prefix}-backup-section`}>
          <div className="mt-6 mb-4">
            <CredentialsSectionTitle>
              Backup credentials
            </CredentialsSectionTitle>
          </div>
          <Form
            descriptionVariant="tooltip"
            formPath={`inputs.${environment}`}
            isConnectionUpdate={isConnectionUpdate}
            prefix={prefix}
            schema={backupCredentialsSchema(schema)}
          />
          <div className="mt-4 flex w-fit">
            <Button
              dataLoc="remove-backup-credentials"
              iconLeft={faTrashCan}
              size="sm"
              variant="secondary"
              onClick={() => onRemoveBackupCredentials(environment)}
            >
              Remove Backup credentials
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-6 flex w-fit">
          <Tooltip
            body={ADD_BACKUP_CREDENTIALS_TOOLTIP}
            footerAction={{
              text: "Learn More",
              onClick: () =>
                window.open(BACKUP_CREDENTIALS_DOCS_URL, "_blank", "noopener"),
            }}
            placement="top"
            asChild
          >
            <Button
              dataLoc="add-backup-credentials"
              iconLeft={faPlus}
              iconRight={faCircleInfo}
              size="sm"
              variant="secondary"
              onClick={() => onAddBackupCredentials(environment)}
            >
              Add Backup credentials
            </Button>
          </Tooltip>
        </div>
      )}
    </>
  );
};

type ManifestEnvironmentsConfigProps = {
  manifest: ManifestIntegrationProvider;
  formValue: ManifestFormType;
  setFormValue: UseFormSetValue<ManifestFormType>;
  connectionToEdit?: Connection | undefined;
};

export const ManifestEnvironmentsConfig = ({
  manifest,
  formValue,
  setFormValue,
  connectionToEdit,
}: ManifestEnvironmentsConfigProps) => {
  const preConfiguredEnvironments =
    connectionToEdit?.environment_configurations;
  const initialEnvironment = preConfiguredEnvironments
    ? Object.keys(preConfiguredEnvironments)[0]
    : undefined;

  const [activeEnvironment, setActiveEnvironment] = useState<
    string | undefined
  >(initialEnvironment);
  // Backup sections start open only for environments that already store
  // backup values; a freshly opened, still-empty section is tracked here.
  const [openBackupSections, setOpenBackupSections] = useState<
    Record<string, boolean>
  >(() =>
    Object.fromEntries(
      Object.entries(formValue.inputs || {}).map(([environment, values]) => [
        environment,
        hasBackupValues(values),
      ]),
    ),
  );
  const availableEnvironments = Object.keys(manifest.environment_configs);
  const flagEnabledEnvironments = availableEnvironments.filter((env) =>
    hasEnvironmentEnabled(manifest.name, env),
  );
  const environmentDisplayNamesMapping = Object.entries(
    manifest.environment_configs,
  ).reduce(
    (acc, [key, value]) => {
      acc[key] = value.display_name || capitalize(key);
      return acc;
    },
    {} as Record<string, string>,
  );
  const addedEnvironments = Object.keys(formValue.inputs || {});
  const form = useFormContext<ManifestFormType>();
  const environmentErrors = form.formState.errors;

  useEffect(() => {
    if (environmentErrors?.inputs) {
      const erroredEnvironments = Object.keys(environmentErrors.inputs);
      if (erroredEnvironments.length > 0) {
        setActiveEnvironment(erroredEnvironments[0]);
      }
    }
  }, [environmentErrors]);

  const hasEnvironmentError = (env: string) => {
    const envErrors = get(environmentErrors, `inputs.${env}`);
    return !!envErrors;
  };

  const handleAddEnvironment = (environment: string) => {
    form.resetField(`inputs.${environment}`);

    setFormValue(`inputs`, {
      ...formValue.inputs,
      [environment]: {},
    });
    setOpenBackupSections((prev) => ({ ...prev, [environment]: false }));
    setActiveEnvironment(environment);
  };

  const handleRemoveEnvironment = (environment: string) => {
    const newInputs = { ...formValue.inputs };
    delete newInputs[environment];
    setFormValue("inputs", newInputs);
    form.clearErrors(`inputs.${environment}`);
    form.unregister(`inputs.${environment}`);
    setOpenBackupSections((prev) => ({ ...prev, [environment]: false }));
    setActiveEnvironment(
      Object.keys(newInputs).length > 0 ? Object.keys(newInputs)[0] : undefined,
    );
  };

  const handleAddBackupCredentials = (environment: string) => {
    setOpenBackupSections((prev) => ({ ...prev, [environment]: true }));
  };

  const handleRemoveBackupCredentials = (environment: string) => {
    const schema = manifest.environment_configs[environment]?.inputs;
    const backupFieldPaths = Object.keys(schema?.properties || {})
      .filter(isBackupInputKey)
      .map((key) => `inputs.${environment}.${key}` as const);
    form.clearErrors(backupFieldPaths);
    form.unregister(backupFieldPaths);
    setFormValue(
      `inputs.${environment}`,
      Object.fromEntries(
        Object.entries(formValue.inputs?.[environment] || {}).filter(
          ([key]) => !isBackupInputKey(key),
        ),
      ),
    );
    setOpenBackupSections((prev) => ({ ...prev, [environment]: false }));
  };

  const isEmptyManifestEnvironmentConfig = Object.values(
    manifest.environment_configs,
  ).every(
    (config) =>
      !config.inputs?.properties ||
      Object.keys(config.inputs.properties).length === 0,
  );

  if (isEmptyManifestEnvironmentConfig) return null;

  if (addedEnvironments.length === 0) {
    return (
      <>
        <NoAddedEnvironmentMessage
          availableEnvironments={flagEnabledEnvironments}
          environmentDisplayNamesMapping={environmentDisplayNamesMapping}
          handleAddEnvironment={handleAddEnvironment}
          providerDisplayName={manifest.display_name}
        />
      </>
    );
  }

  return (
    <>
      <Tabs
        containerClassName="flex flex-col"
        customButton={
          flagEnabledEnvironments.filter(
            (env) => !addedEnvironments.includes(env),
          ).length > 0 ? (
            <EnvironmentDropDown
              addedEnvironments={addedEnvironments}
              availableEnvironments={flagEnabledEnvironments}
              environmentDisplayNamesMapping={environmentDisplayNamesMapping}
              handleAddEnvironment={handleAddEnvironment}
            />
          ) : undefined
        }
        panelClassName="h-full flex flex-col rounded-lg bg-gray-50 px-4 pt-3 pb-6"
        panelsClassName="flex-1"
        selectedKey={activeEnvironment}
        tabClassName="px-0 mr-4 flex-shrink-0 cursor-pointer"
        tabListClassName="border-b border-gray-200 mb-4 sticky -top-5 bg-white z-10 flex"
        tabs={availableEnvironments
          .filter((env) => addedEnvironments.includes(env))
          .map((environment) => ({
            key: environment,
            label: (
              <div className="flex items-center gap-2">
                <span>{environmentDisplayNamesMapping[environment]}</span>
                {hasEnvironmentError(environment) && (
                  <Icon
                    variant="error"
                    icon={faExclamationTriangle}
                    size="sm"
                  />
                )}
              </div>
            ),
            content: (
              <EnvironmentCredentialsFields
                environment={environment}
                environmentDisplayName={
                  environmentDisplayNamesMapping[environment] ||
                  capitalize(environment)
                }
                isBackupSectionOpen={!!openBackupSections[environment]}
                isConnectionUpdate={!!connectionToEdit}
                schema={manifest.environment_configs[environment].inputs}
                onAddBackupCredentials={handleAddBackupCredentials}
                onRemoveBackupCredentials={handleRemoveBackupCredentials}
                onRemoveEnvironment={handleRemoveEnvironment}
              />
            ),
          }))}
        onChange={setActiveEnvironment}
      />
      <Divider spacing="my-4" />
    </>
  );
};
